import { AddOnCategory, AddOnType, SubscriptionTier } from '../../enums/clientEnums';
import type { SubscriptionType } from '../../types/ClientManagementTypes';

// Steps of the create/edit client form, in tab order.
export enum ClientFormStep {
  CLIENT = 'client',
  SUBSCRIPTION = 'subscription',
  PAYMENT = 'payment',
  SUMMARY = 'summary',
}

// Dispatch ids for the row ⋮ menu and the header actions.
export enum ClientAction {
  ADD_CLIENT = 'add_client',
  EDIT_CLIENT = 'edit_client',
  ARCHIVE_CLIENT = 'archive_client',
  RESTORE_CLIENT = 'restore_client',
  DELETE_CLIENT = 'delete_client',
}

// Values are ServiceBadge's SCSS class names.
export enum BadgeTone {
  GREY = 'grey',
  TEAL = 'teal',
  TRANSPARENT = 'transparent',
  STORAGE = 'storage',
  USERS = 'users',
  MAPS = 'maps',
  MODULES = 'modules',
  NEUTRAL = 'neutral',
}

// An archived client's only way back is Restore, so it takes Archive's slot.
export const rowOptionsFor = (archived: boolean) => [
  { id: ClientAction.EDIT_CLIENT, label: 'Edit Client' },
  archived ? { id: ClientAction.RESTORE_CLIENT, label: 'Restore Client' } : { id: ClientAction.ARCHIVE_CLIENT, label: 'Archive Client' },
  { id: ClientAction.DELETE_CLIENT, label: 'Delete Client' },
];

const SUBSCRIPTION_TONE: Record<SubscriptionTier, BadgeTone> = {
  [SubscriptionTier.DEMO]: BadgeTone.TRANSPARENT,
  [SubscriptionTier.BASIC]: BadgeTone.GREY,
  [SubscriptionTier.PREMIUM]: BadgeTone.TEAL,
};

// Colour is keyed on category, not code, so a new add-on inherits its category's colour.
const CATEGORY_TONE: Record<AddOnCategory, BadgeTone> = {
  [AddOnCategory.STORAGE]: BadgeTone.STORAGE,
  [AddOnCategory.USERS]: BadgeTone.USERS,
  [AddOnCategory.MAPS]: BadgeTone.MAPS,
  [AddOnCategory.MODULES]: BadgeTone.MODULES,
};

const ADD_ON_CATEGORY: Record<AddOnType, AddOnCategory> = {
  [AddOnType.STORAGE]: AddOnCategory.STORAGE,
  [AddOnType.ADMIN]: AddOnCategory.USERS,
  [AddOnType.EDITOR]: AddOnCategory.USERS,
  [AddOnType.MAPS]: AddOnCategory.MAPS,
  [AddOnType.INFRASTRUCTURE]: AddOnCategory.MODULES,
};

// Headers of the add-on picker's accordion; panel order follows the enum.
export const ADD_ON_CATEGORY_LABEL: Record<AddOnCategory, string> = {
  [AddOnCategory.STORAGE]: 'DATA STORAGE',
  [AddOnCategory.USERS]: 'USERS',
  [AddOnCategory.MAPS]: 'MAPS',
  [AddOnCategory.MODULES]: 'MODULES',
};

export const addOnCategory = (addonType: AddOnType | string): AddOnCategory | undefined => ADD_ON_CATEGORY[addonType as AddOnType];

// Both fall back rather than index blindly: the backend can ship a tier or an
// add-on before we map it, and it must still render as a pill.
export const subscriptionTone = (tier: SubscriptionTier): BadgeTone => SUBSCRIPTION_TONE[tier] ?? BadgeTone.NEUTRAL;

export const addOnTone = (addonType: AddOnType | string): BadgeTone => {
  const category = addOnCategory(addonType);
  return category ? CATEGORY_TONE[category] : BadgeTone.NEUTRAL;
};

// The mockups label tiers by pill colour rather than plan name; keeping that
// here leaves the domain enum honest.
const SUBSCRIPTION_LABEL: Record<SubscriptionTier, string> = {
  [SubscriptionTier.DEMO]: 'Demo',
  [SubscriptionTier.BASIC]: 'Grey',
  [SubscriptionTier.PREMIUM]: 'Teal',
};

export const subscriptionLabel = (tier: SubscriptionTier) => SUBSCRIPTION_LABEL[tier] ?? tier;

// The plan catalogue names a tier; the colour map is only the fallback until it loads.
export const subscriptionLabelFrom = (typesById: Map<SubscriptionTier, SubscriptionType>, tier: SubscriptionTier) =>
  typesById.get(tier)?.name ?? subscriptionLabel(tier);

export const DISPLAY_DATE_FORMAT = 'MMMM D, YYYY';

/** Used is always shown to one decimal, the allowance as a whole number. */
export const formatStorage = (usedGb: number, totalGb: number) => `${usedGb.toFixed(1)}GB/${totalGb}GB`;

export const formatCost = (cost: number) => `$${cost}`;

// Feeds antd's <colgroup>; the add-on table mirrors these so the two grids align.
export const COLUMN_WIDTH = {
  id: 72,
  name: 220,
  adminEmail: 220,
  subscription: 160,
  expiration: 180,
  storage: 150,
  cost: 120,
  status: 120,
  actions: 48,
};

// Ordered, for the add-on table to mirror by position rather than by field name.
export const CLIENT_COLUMN_WIDTHS = Object.values(COLUMN_WIDTH);

// Below this the table scrolls sideways, above it it fills its collapse. Must be
// a number: rc-table writes scroll.x as an inline width.
export const TABLE_MIN_WIDTH_PX = CLIENT_COLUMN_WIDTHS.reduce((total, width) => total + width, 0);

// Mirrors --dt-row-height on .clientsTable; px because scroll.y takes a number.
const ROW_HEIGHT_PX = 56;

// The sliver of a fifth row is what tells the user there is more to scroll.
export const TABLE_BODY_HEIGHT_PX = ROW_HEIGHT_PX * 4.25;

export const EMPTY_COPY = {
  NO_GROUPS: 'No clients present.',
  NO_CLIENTS_IN_GROUP: 'No clients in this group.',
  NO_SEARCH_MATCHES: 'No matching clients found.',
  NO_ADD_ONS: 'No add-ons.',
  ADD_ONS_ERROR: 'Unable to load add-ons.',
  SEARCH_ERROR: 'Unable to perform search. Please try again.',
  LOAD_ERROR: 'Unable to load clients. Please try again.',
};
